'use client';

import { c as _c } from "react/compiler-runtime";
import { Spinner } from '@common/components';
import { SvgManual } from '@common/icon';
import { cn } from '@core/utils';
import type { DetailHomeInstitutionResponse, ListHomeGiftProductByInstitutionResponse } from '@generated/unauth';
import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { CustomLink } from '@/app/_components/CustomLink';
import { CartIconLink } from '@/app/donation/_components/CartIconLink';
import { PageNavigation } from '@/components/client';
import { DONATION_ROUTES } from '@/constants';
import { useEventListTotalCount } from '../_hooks/useEventListTotalCount';
import { HOMETOWNS_DETAIL_TAB } from '../constants';
import { AppBack } from './AppBack';
import { BenefitsContent } from './BenefitsContent';
import { ContentsContent } from './ContentsContent';
import { GiftsContent } from './GiftsContent';
import { HometownsTab } from './HometownsTab';
import { MainInfoSection } from './MainInfoSection';
import { PolicyContent } from './PolicyContent';
import { ProjectsContent } from './ProjectsContent';
import { ReviewsContent } from './ReviewsContent';
const SectionSpinner = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="py-40 flex-center"><Spinner /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
interface HometownsDetailTemplateProps {
  institutionId: number;
  institutionData: DetailHomeInstitutionResponse;
  giftProductData?: ListHomeGiftProductByInstitutionResponse;
  hasProjects: boolean;
  hasContents: boolean;
  hasPolicy: boolean;
  hasReviews: boolean;
}
export const HometownsDetailTemplate = t0 => {
  const $ = _c(47);
  const {
    institutionId,
    institutionData,
    giftProductData,
    hasProjects,
    hasContents,
    hasPolicy,
    hasReviews
  } = t0;
  const {
    data: benefitsCount
  } = useEventListTotalCount(institutionId);
  const hasBenefits = (benefitsCount ?? 0) > 0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <AppBack />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <CartIconLink />;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <PageNavigation donationHomeButton={true} isNotUsedHomeButton={true}>{t2}<CustomLink href={DONATION_ROUTES.GUIDE.pathname} aria-label={"\uAC00\uC774\uB4DC \uD398\uC774\uC9C0\uB85C \uC774\uB3D9"}><SvgManual className="size-24 text-Gray-900" /></CustomLink></PageNavigation>;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== institutionData.backgroundVideoImageUrl || $[4] !== institutionData.description || $[5] !== institutionData.institutionName || $[6] !== institutionData.logoUrl || $[7] !== institutionData.pcBackgroundVideoImageUrl || $[8] !== institutionData.summary || $[9] !== institutionId) {
    t4 = <MainInfoSection title={institutionData.institutionName} summary={institutionData.summary} description={institutionData.description} institutionId={institutionId} backgroundVideoImageUrl={institutionData.backgroundVideoImageUrl} pcBackgroundVideoImageUrl={institutionData.pcBackgroundVideoImageUrl} logoUrl={institutionData.logoUrl} />;
    $[3] = institutionData.backgroundVideoImageUrl;
    $[4] = institutionData.description;
    $[5] = institutionData.institutionName;
    $[6] = institutionData.logoUrl;
    $[7] = institutionData.pcBackgroundVideoImageUrl;
    $[8] = institutionData.summary;
    $[9] = institutionId;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = cn("sticky top-0 z-10 mt-40 bg-transparent px-40 content-container", "mobile-tablet:mt-24 mobile-tablet:px-0");
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== hasBenefits || $[13] !== hasContents || $[14] !== hasPolicy || $[15] !== hasProjects || $[16] !== hasReviews) {
    t6 = filterTabs({
      hasProjects,
      hasContents,
      hasPolicy,
      hasReviews,
      hasBenefits
    });
    $[12] = hasBenefits;
    $[13] = hasContents;
    $[14] = hasPolicy;
    $[15] = hasProjects;
    $[16] = hasReviews;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t6) {
    t7 = <header className={t5}><HometownsTab tabs={t6} /></header>;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== giftProductData || $[21] !== institutionId) {
    t8 = giftProductData && giftProductData.totalCount > 0 ? <section id={HOMETOWNS_DETAIL_TAB.GIFTS} className={cn("mb-80 px-40 content-container", "mobile-tablet:mb-56 mobile-tablet:px-20")}><ErrorBoundary fallback={null}><Suspense fallback={<SectionSpinner />}><GiftsContent institutionId={institutionId} initialData={giftProductData.contents} /></Suspense></ErrorBoundary></section> : null;
    $[20] = giftProductData;
    $[21] = institutionId;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== hasProjects || $[24] !== institutionId) {
    t9 = hasProjects ? <section id={HOMETOWNS_DETAIL_TAB.PROJECTS} className={cn("mb-80 px-40 content-container", "mobile-tablet:mb-56 mobile-tablet:px-20")}><ProjectsContent institutionId={institutionId} /></section> : null;
    $[23] = hasProjects;
    $[24] = institutionId;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== hasContents || $[27] !== institutionId) {
    t10 = hasContents ? <section id={HOMETOWNS_DETAIL_TAB.CONTENTS} className={cn("mb-80 px-40 content-container", "mobile-tablet:mb-56 mobile-tablet:px-20")}><ContentsContent institutionId={institutionId} /></section> : null;
    $[26] = hasContents;
    $[27] = institutionId;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== hasPolicy || $[30] !== institutionId) {
    t11 = hasPolicy ? <section id={HOMETOWNS_DETAIL_TAB.POLICY} className={cn("mb-80 px-40 content-container", "mobile-tablet:mb-56 mobile-tablet:px-20")}><PolicyContent institutionId={institutionId} /></section> : null;
    $[29] = hasPolicy;
    $[30] = institutionId;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== hasReviews || $[33] !== institutionId) {
    t12 = hasReviews ? <section id={HOMETOWNS_DETAIL_TAB.REVIEWS} className={cn("mb-80 px-40 content-container", "mobile-tablet:mb-56 mobile-tablet:px-20")}><ErrorBoundary fallback={null}><Suspense fallback={<SectionSpinner />}><ReviewsContent institutionId={institutionId} /></Suspense></ErrorBoundary></section> : null;
    $[32] = hasReviews;
    $[33] = institutionId;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  let t13;
  if ($[35] !== hasBenefits || $[36] !== institutionId) {
    t13 = hasBenefits ? <section id={HOMETOWNS_DETAIL_TAB.BENEFITS} className={cn("mb-80 px-40 content-container", "mobile-tablet:mb-56 mobile-tablet:px-20")}><BenefitsContent institutionId={institutionId} /></section> : null;
    $[35] = hasBenefits;
    $[36] = institutionId;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  let t14;
  if ($[38] !== t10 || $[39] !== t11 || $[40] !== t12 || $[41] !== t13 || $[42] !== t4 || $[43] !== t7 || $[44] !== t8 || $[45] !== t9) {
    t14 = <div className="relative">{t1}{t3}{t4}{t7}{t8}{t9}{t10}{t11}{t12}{t13}</div>;
    $[38] = t10;
    $[39] = t11;
    $[40] = t12;
    $[41] = t13;
    $[42] = t4;
    $[43] = t7;
    $[44] = t8;
    $[45] = t9;
    $[46] = t14;
  } else {
    t14 = $[46];
  }
  return t14;
};
const filterTabs = ({
  hasProjects,
  hasContents,
  hasPolicy,
  hasReviews,
  hasBenefits
}: {
  hasProjects: boolean;
  hasContents: boolean;
  hasPolicy: boolean;
  hasReviews: boolean;
  hasBenefits: boolean;
}) => {
  const tabs: ({
    title: string;
    tab: HOMETOWNS_DETAIL_TAB;
  } | null)[] = [{
    title: '답례품',
    tab: HOMETOWNS_DETAIL_TAB.GIFTS
  }, hasProjects ? {
    title: '지정기부',
    tab: HOMETOWNS_DETAIL_TAB.PROJECTS
  } : null, hasContents ? {
    title: '콘텐츠',
    tab: HOMETOWNS_DETAIL_TAB.CONTENTS
  } : null, hasPolicy ? {
    title: '정책',
    tab: HOMETOWNS_DETAIL_TAB.POLICY
  } : null, hasReviews ? {
    title: '후기',
    tab: HOMETOWNS_DETAIL_TAB.REVIEWS
  } : null, hasBenefits ? {
    title: '혜택',
    tab: HOMETOWNS_DETAIL_TAB.BENEFITS
  } : null];
  return tabs.filter((tab): tab is {
    title: string;
    tab: HOMETOWNS_DETAIL_TAB;
  } => Boolean(tab));
};